import React, { useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import { v4 as uuidv4 } from "uuid";
import { ApolloError, useMutation } from "@apollo/client";
import {
  CREATE_ENFORCEMENT_INTENT,
  CreateEnforcementIntentData,
  CreateEnforcementIntentVars,
  GENERATE_ENFORCEMENT_PHOTO_UPLOAD_URL,
  GenerateEnforcementPhotoUploadUrlData,
  GenerateEnforcementPhotoUploadUrlVars,
} from "common/graphql";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import { useSnackPackContext } from "@app.automotus.io/components/context/SnackPack";
import { useFeatureFlags, useIsMobile, useUserProfile } from "@app.automotus.io/components/hooks";
import { FEATURE_FLAGS } from "@curbsuite/constants";
import { EvidenceCapture, EvidenceCaptureValues } from "./sections/EvidenceCapture/EvidenceCapture";
import { EvidencePhotoUploadError, uploadEvidencePhoto } from "./EvidencePhotoUpload/useEvidencePhotoUpload";
import { PrefilledDetails } from "./sections/PrefilledDetails/PrefilledDetails";
import { Vehicle, VehicleValues } from "./sections/Vehicle/Vehicle";
import { NoticeDetails, NoticeDetailsValues } from "./sections/NoticeDetails/NoticeDetails";
import { ViolationPenalty } from "./sections/ViolationPenalty/ViolationPenalty";
import { useViolationTypes } from "./useViolationTypes";

export type GenerateCitationFormValues = VehicleValues & EvidenceCaptureValues & NoticeDetailsValues;

const initialValues: GenerateCitationFormValues = {
  ...Vehicle.defaultInitialValues,
  ...EvidenceCapture.defaultInitialValues,
  ...NoticeDetails.defaultInitialValues,
};

// One schema covering every section's fields, matching the convention used by every other
// multi-field form in this codebase (see e.g. AddVehicleExemptionDialog.tsx) -- Formik itself
// blocks a submit attempt and marks every field touched when this fails, so onSubmit below can
// trust values.violationCode/location/licensePlatePhoto/violationPhoto are already valid by the
// time it runs, rather than re-checking them by hand.
const validationSchema = Vehicle.validationSchema
  .concat(NoticeDetails.validationSchema)
  .concat(EvidenceCapture.validationSchema);

export interface GenerateCitationProps {
  /**
   * Demo/preview-only overrides. The real route (`routes.tsx`) always renders
   * `<GenerateCitation />` with none of these, so production behavior is unchanged --
   * they exist so Storybook can preview what this form looks like once the pieces
   * blocked on AUT-5574 (computed penalty, city) actually exist.
   */
  city?: string;
  initialValuesOverride?: Partial<GenerateCitationFormValues>;
}

export const GenerateCitation: React.FC<GenerateCitationProps> = ({ city, initialValuesOverride }) => {
  const navigate = useNavigate();
  const { publishSnackbarMessage } = useSnackPackContext();
  const [generatePhotoUploadUrl] = useMutation<
    GenerateEnforcementPhotoUploadUrlData,
    GenerateEnforcementPhotoUploadUrlVars
  >(GENERATE_ENFORCEMENT_PHOTO_UPLOAD_URL);
  const [createEnforcementIntent] = useMutation<CreateEnforcementIntentData, CreateEnforcementIntentVars>(
    CREATE_ENFORCEMENT_INTENT,
  );
  // Computed before the eligibility redirect below (rather than after) so it can gate
  // useViolationTypes -- otherwise this hook, and the enforcement-policy request it fires,
  // would run on every mount regardless of whether the session is even allowed on this screen,
  // since hooks have to be called unconditionally. The feature flag reads as off while userProfile
  // is still loading, so this briefly under-reports eligibility during the loading state below,
  // which is fine -- it just delays the fetch by one render, it never wrongly fires it early. The
  // mobile check is because the form isn't built for desktop.
  const isMobile = useIsMobile();
  const { loading } = useUserProfile();
  const { isEnabled } = useFeatureFlags();
  const isEligibleForManualCitations = isMobile && isEnabled(FEATURE_FLAGS.MANUAL_CITATIONS_IS_ENABLED);
  const {
    options: violationTypeOptions,
    loading: violationTypesLoading,
    error: violationTypesError,
    refetch: refetchViolationTypes,
  } = useViolationTypes(isEligibleForManualCitations);
  // useEvidencePhotoUpload's error/previewUrl state lives inside EvidenceCapture's two
  // EvidencePhotoUpload instances, outside Formik -- resetForm() alone can't clear it.
  // Remounting via this key wipes both hooks' internal state along with the form fields.
  const [evidenceResetKey, setEvidenceResetKey] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Caches each photo's uploaded imageId by File identity, so retrying a submission that failed
  // partway through (one photo uploaded, then the other upload or the create-intent call fails)
  // reuses the already-uploaded photo instead of re-uploading it -- otherwise the first upload is
  // orphaned in S3 (never referenced by any citation) every time a retry allocates a fresh imageId
  // for it. Cleared on Clear Form, since a fresh form should never reuse a photo from a previous
  // attempt even if the same file were somehow re-selected.
  const uploadedImageIdsRef = useRef(new Map<File, string>());

  const getOrUploadImageId = async (file: File, signal: AbortSignal): Promise<string> => {
    const cached = uploadedImageIdsRef.current.get(file);
    if (cached) {
      return cached;
    }
    const imageId = await uploadEvidencePhoto(generatePhotoUploadUrl, file, signal);
    uploadedImageIdsRef.current.set(file, imageId);
    return imageId;
  };

  if (loading) {
    return null;
  }

  if (!isEligibleForManualCitations) {
    return <Navigate replace to="/notices" />;
  }

  return (
    <Stack spacing={3} sx={{ p: 2 }}>
      <Typography variant="h4" fontWeight={600} letterSpacing={0.25}>
        Generate Citation
      </Typography>
      <Formik<GenerateCitationFormValues>
        initialValues={{ ...initialValues, ...initialValuesOverride }}
        validationSchema={validationSchema}
        // Without this, Formik's `isValid` reads true until something actually triggers
        // validation (a blur, or a submit attempt) -- misleadingly so, since an all-empty form
        // is not valid. Issue Citation's disabled state below depends on `isValid` being correct
        // from the very first render, not just after the officer has already touched a field.
        validateOnMount
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitError(null);

          // Recomputed here (rather than shared with the render prop's `selectedViolationType`
          // below, used for the live ViolationPenalty total) because onSubmit is a prop passed
          // to <Formik>, defined outside that render prop's scope -- it only has its own
          // `values` argument, not `formik.values`. Both derive from the same
          // `violationTypeOptions` closed over from this component, so there's no real
          // duplication, just the same one-line lookup done twice against the same source.
          //
          // validationSchema already guarantees values.violationCode is a non-empty string by
          // the time onSubmit runs, but not that it still matches a currently loaded option (the
          // options list is fetched once and could in principle change under a very long-lived
          // form) -- .find() can still come back empty in that edge case, so this check stays as
          // a narrow type-safety/defensive backstop, not the primary "is something selected" gate
          // that it used to be.
          const selectedViolationType = violationTypeOptions.find((option) => option.policyId === values.violationCode);
          if (
            !selectedViolationType ||
            !values.licensePlatePhoto?.file ||
            !values.violationPhoto?.file ||
            !values.violationDateTime
          ) {
            setSubmitting(false);
            return;
          }

          const violationAt = values.violationDateTime;

          // Shared between both uploads below so that if one fails, the other's still-in-flight
          // S3 PUT gets cancelled instead of being left to run to completion in the background --
          // otherwise Promise.all rejecting as soon as the first upload fails doesn't stop the
          // second one, and it can still land in S3 with no citation ever created for it (see the
          // "orphaned upload" note above, and useEvidencePhotoUpload.ts's putFile).
          const uploadAbortController = new AbortController();

          try {
            // Photos are only uploaded now, at submission time -- not as soon as the officer
            // picks them -- so an abandoned or cleared form never leaves a stray, citation-less
            // upload in S3. Uploaded together since neither depends on the other. Goes through
            // getOrUploadImageId (not uploadEvidencePhoto directly) so retrying after a partial
            // failure reuses whichever photo(s) already succeeded instead of re-uploading them.
            const [licensePlateImageId, violationImageId] = await Promise.all([
              getOrUploadImageId(values.licensePlatePhoto.file, uploadAbortController.signal),
              getOrUploadImageId(values.violationPhoto.file, uploadAbortController.signal),
            ]);

            const { data } = await createEnforcementIntent({
              variables: {
                enforcementPolicyId: selectedViolationType.policyId,
                violationAt,
                licensePlateNumber: values.licensePlate,
                // Hardcoded "us-" prefix -- same assumption as AddVehicleExemptionDialog.tsx and the rest of
                // licensePlateRegion's "us-xx" callers. Will need to change once this flow supports
                // international markets, where licensePlateState won't be a US state.
                licensePlateRegion: values.licensePlateState
                  ? `us-${values.licensePlateState.toLowerCase()}`
                  : undefined,
                vehicleMake: values.vehicleMake || undefined,
                vehicleModel: values.vehicleModel || undefined,
                vehicleColor: values.vehicleColor || undefined,
                officerNotes: values.officerNotes || undefined,
                streetAddress: values.location,
                licensePlateImageId,
                violationImageId,
                sessionId: uuidv4(),
              },
            });
            const createdIntent = data?.create_enforcement_intent;

            // A same-plate-within-30-minutes duplicate is auto-voided server-side but still
            // comes back as a normal mutation result (AUT-5749) -- this is the one case where a
            // "successful" response isn't actually success from the officer's point of view, so
            // don't navigate away: that would unmount this component (and the message with it)
            // before they could read it. Every other response shape (a real, non-voided intent)
            // is the normal success path.
            if (createdIntent?.status === "voided" && createdIntent.voidedReason === "duplicate_citation") {
              // Both photos were consumed server-side as part of creating (then voiding) this
              // intent, so the cached ids from getOrUploadImageId are already burned -- without
              // clearing them, retrying without touching the photo widgets would resend the
              // same now-consumed ids and fail with an unrelated "already used" error instead of
              // just re-uploading fresh ones.
              uploadedImageIdsRef.current.clear();
              setSubmitError(
                "A citation for this plate was already issued in the last 30 minutes. This submission wasn't created to avoid duplicating it.",
              );
              return;
            }

            publishSnackbarMessage({
              message: "Citation issued successfully",
              severity: "success",
              // SnackbarAlert defaults to bottom-center on mobile (the only viewport this form
              // renders on) -- top-center instead, since the officer lands on /notices right
              // after this fires and shouldn't have to look down to notice it.
              anchorOrigin: { vertical: "top", horizontal: "center" },
            });
            navigate("/notices?table=all");
          } catch (err) {
            // No-op if both uploads already succeeded (createEnforcementIntent failed instead) --
            // aborting a controller whose fetch has already resolved does nothing. If one upload
            // failed, this stops its sibling immediately rather than letting it finish uploading
            // into the void.
            uploadAbortController.abort();
            console.error("Failed to submit citation", err);

            // The create-intent action's DB write (including marking both photos consumed) can
            // commit even though this request still surfaces as a failure here -- the action's
            // 15s timeout (actions.yaml) can elapse, or a mobile connection can drop, after the
            // transaction lands but before the response arrives. When that happens, the cached
            // ids from getOrUploadImageId are already burned server-side: without clearing them
            // here too (mirroring the duplicate-citation branch above), every retry would resend
            // the same now-consumed ids and hit this exact error again, with Clear Form (and
            // re-shooting both photos) the only way out.
            if (
              err instanceof ApolloError &&
              err.graphQLErrors[0]?.message?.includes("has already been used on another citation")
            ) {
              uploadedImageIdsRef.current.clear();
            }

            setSubmitError(
              // Both Hasura requests in this flow (generate_enforcement_photo_upload_url and
              // create_enforcement_intent) fail as an ApolloError. A network-level failure (DNS,
              // offline) surfaces as its networkError being the browser-native TypeError fetch()
              // rejected with -- checked by type rather than by err.message, which varies by
              // browser ("Failed to fetch" in Chrome, "NetworkError..." in Firefox, "Load failed"
              // in Safari) and would silently mis-detect on anything but Chrome. A failed action
              // instead carries the API's own client-facing text (e.g. "the submitting officer has
              // no badge ID configured") via graphQLErrors -- both actions' response_transforms
              // always set a message, "Unknown error" at worst, on a non-2xx response -- worth
              // showing directly rather than flattening into a generic message. The actual S3 PUT
              // (its own fetch call, an unauthenticated binary PUT direct to a presigned S3 URL
              // rather than through Hasura) throws its own EvidencePhotoUploadError instead, with a
              // message already written for the officer, and never lets a raw TypeError escape --
              // see useEvidencePhotoUpload.ts's putFile.
              err instanceof ApolloError && err.networkError instanceof TypeError
                ? "No connection. Check your network and try again."
                : err instanceof EvidencePhotoUploadError
                ? err.message
                : err instanceof ApolloError
                ? err.graphQLErrors[0]?.message ?? err.message
                : "Something went wrong submitting this citation. Try again.",
            );
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {(formik) => {
          const selectedViolationType = violationTypeOptions.find(
            (option) => option.policyId === formik.values.violationCode,
          );
          const penalty = selectedViolationType
            ? { status: "unpaid" as const, amountCents: selectedViolationType.amountDueCents }
            : undefined;

          return (
            <Form>
              <Stack spacing={2}>
                <EvidenceCapture key={evidenceResetKey} />
                <PrefilledDetails city={city} violationCode={selectedViolationType?.label} />
                <Vehicle />
                <NoticeDetails
                  violationTypeOptions={violationTypeOptions}
                  violationTypesLoading={violationTypesLoading}
                  violationTypesError={violationTypesError}
                  onRetryViolationTypes={refetchViolationTypes}
                />
                <ViolationPenalty penalty={penalty} />
                {submitError && (
                  <Alert severity="error" onClose={() => setSubmitError(null)}>
                    {submitError}
                  </Alert>
                )}
                <Stack direction="row" spacing={2}>
                  <Button
                    fullWidth
                    disabled={formik.isSubmitting}
                    onClick={() => {
                      formik.resetForm();
                      // resetForm() resets `errors` to empty along with `values`, but doesn't
                      // revalidate -- without this, isValid reads true against the just-cleared
                      // form until the officer's first keystroke happens to trigger
                      // validateOnChange, and Issue Citation stays wrongly enabled the whole time.
                      // Passed formik.initialValues explicitly (rather than the no-arg form, which
                      // reads current state) since resetForm's own state update hasn't committed
                      // yet in this same handler -- validateForm() with no argument would validate
                      // the pre-reset (still fully filled in, still valid) values instead.
                      formik.validateForm(formik.initialValues);
                      setEvidenceResetKey((key) => key + 1);
                      setSubmitError(null);
                      uploadedImageIdsRef.current.clear();
                    }}
                  >
                    Clear Form
                  </Button>
                  <LoadingButton
                    fullWidth
                    type="submit"
                    variant="contained"
                    loading={formik.isSubmitting}
                    // Disabled (rather than left clickable into a guard message) whenever
                    // there's genuinely nothing to submit yet: still loading, no violation types
                    // configured at all (an officer who can't even see the dropdown shouldn't be
                    // told to use it), or the form itself isn't complete/valid yet -- !isValid
                    // covers every required field across Vehicle, NoticeDetails, and
                    // EvidenceCapture's combined validationSchema (plate, state, make, model,
                    // color, violation code, location, both evidence photos) in one condition,
                    // rather than re-deriving "is the form filled out" by hand here.
                    disabled={violationTypesLoading || violationTypeOptions.length === 0 || !formik.isValid}
                  >
                    Issue Citation
                  </LoadingButton>
                </Stack>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </Stack>
  );
};

export default GenerateCitation;
